import React, { memo, useCallback, useEffect } from 'react';
import styled from 'styled-components';
import { Typography } from '../../../../Typography/Typography';
import { Button } from '../../../../Button/Button';
import InfoBox from '../../../../InfoBox/InfoBox';
import { QueryModel, SortModel } from '../../../utils/DataTableQueryProvider/DataTableQuery.types';
import { useDataTableQuickReportsContext } from '../../../utils/DataTableQuickReportsContext';
import { useDataTableFilterContext } from '../../../utils/DataTableFilterContext';
import { useDataTableContext } from '../../../utils/DataTableContext';
import { AddQuickReportDialog } from '../Dialog/AddQuickReportDialog';
import { DeleteQuickReportDialog } from '../Dialog/DeleteQuickReportDialog';
import { useDataTableQuickActionsContext } from '../../../utils/DataTableQuickActionsContext';
import { DnDQuickReports } from './DnDQuickReports';
import { useIntlUds } from '../../../../../translations/UdsIntlProvider';

const DataTableQuickReportsContentComponent = <Q extends QueryModel, S extends SortModel>() => {
  const {
    quickReports,
    activeQuickReportId,
    unSavedQuickReport,
    setQuickReportDrawer,
    isQuickReportDrawerOpen,
    newQuickReportId,
    setNewQuickReportId
  } = useDataTableQuickReportsContext<Q, S>();
  const { setOpenFilterDrawer } = useDataTableFilterContext();
  const { isFetching } = useDataTableContext();

  const { intl } = useIntlUds();

  const {
    quickReport,
    setQuickReport,
    saveNewQuickReport,
    editQuickReportName,
    duplicateQuickReport,
    deleteQuickReport,
    saveAsQuickReport
  } = useDataTableQuickActionsContext();

  const goToFilterPanel = useCallback(() => {
    setQuickReportDrawer(false);
    setOpenFilterDrawer(true);
  }, [setOpenFilterDrawer, setQuickReportDrawer]);

  useEffect(() => {
    if (newQuickReportId && newQuickReportId === activeQuickReportId && !isFetching && isQuickReportDrawerOpen) {
      goToFilterPanel();
      setNewQuickReportId(undefined);
    }
  }, [
    newQuickReportId,
    goToFilterPanel,
    isFetching,
    isQuickReportDrawerOpen,
    activeQuickReportId,
    setNewQuickReportId
  ]);

  const onSaveHandler = useCallback(() => {
    if (quickReport?.actionType === 'Rename') {
      editQuickReportName();
    }
    if (quickReport?.actionType === 'Add') {
      saveNewQuickReport();
    }
    if (quickReport?.actionType === 'Duplicate') {
      duplicateQuickReport();
    }
    if (quickReport?.actionType === 'Save as') {
      saveAsQuickReport();
    }
  }, [quickReport?.actionType, saveAsQuickReport, editQuickReportName, saveNewQuickReport, duplicateQuickReport]);

  if (!Array.isArray(quickReports)) return null;

  const isUnSavedState = !!unSavedQuickReport || !!newQuickReportId;

  return (
    <>
      <AddQuickReportDialog saveQuickReport={quickReport} setSaveQuickReport={setQuickReport} onSave={onSaveHandler} />
      <DeleteQuickReportDialog
        saveQuickReport={quickReport}
        setSaveQuickReport={setQuickReport}
        onDelete={deleteQuickReport}
      />
      {isUnSavedState && (
        <InfoBox
          dataTest="warning-disable-actions"
          type="ALERT"
          title={intl.DataTable.QuickViews.Drawer.unSavedBox.title}
        >
          <Typography>{intl.DataTable.QuickViews.Drawer.unSavedBox.description}</Typography>
          <StyledButtonWrapper>
            <Button dataTest="go-to-filter-panel-button" color="primary" variant="contained" onClick={goToFilterPanel}>
              {intl.DataTable.QuickViews.Drawer.unSavedBox.action}
            </Button>
          </StyledButtonWrapper>
        </InfoBox>
      )}

      <StyledNoQuickReports data-test="add-quick-reports-info">
        <Button
          dataTest="create-quick-report-button"
          color="primary"
          variant="outlined"
          disabled={isUnSavedState || isFetching}
          onClick={() => setQuickReport({ name: '', actionType: 'Add' })}
        >
          {intl.DataTable.QuickViews.Drawer.createQuickViewBtn}
        </Button>
      </StyledNoQuickReports>
      {quickReports.length === 0 && (
        <StyledTypography dataTest="no-quick-reports-info">
          {intl.DataTable.QuickViews.Drawer.noQuickViewMessage}
        </StyledTypography>
      )}
      <DnDQuickReports
        setNewQuickReportId={setNewQuickReportId}
        goToFilterPanel={goToFilterPanel}
        isUnSavedState={isUnSavedState}
      />
    </>
  );
};

export const DataTableQuickReportsContent = memo(DataTableQuickReportsContentComponent);

const StyledTypography = styled(Typography)`
  && {
    margin-top: ${({ theme }) => theme.spacingSize.xl};
    &.body1 {
      text-align: center;
    }
  }
`;

const StyledButtonWrapper = styled.div`
  width: 100%;
  display: flex;
  justify-content: flex-end;
`;

const StyledNoQuickReports = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  padding: ${({ theme }) => theme.spacingSize.l};
  margin-top: ${({ theme }) => theme.spacingSize.l};
  border: ${({ theme }) => `1px solid ${theme.colorSurfaceVariant}`};
  border-radius: ${({ theme }) => theme.radiusDefault};
  box-sizing: border-box;
`;
